import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExternalLinkAlt,
  faExclamationTriangle,
  faWrench,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";

import bakedBlissImg from "../assets/images/baked_bliss.png";
import studyBlissImg from "../assets/images/Study_Bliss.png";
import booksImg from "../assets/images/Books.png";
import wiseWayImg from "../assets/images/wise_way.png";

const PROJECTS = [
  {
    number: "01",
    title: "Baked Bliss Bakery",
    category: "Fullstack E-Commerce",
    badge: "Client Project",
    image: bakedBlissImg,
    alt: "Baked Bliss Bakery enterprise e-commerce platform",
    description:
      "Full-stack multi-tenant bakery e-commerce platform featuring real-time profile synchronization, dynamic ordering catalog, and zero-downtime payments.",
    challenge:
      "Client required a scalable platform to onboard multiple store locations with role-based access, inventory tracking, and seamless payment workflows.",
    solution:
      "Architected a Next.js and Node.js backend with PostgreSQL, Stripe payment webhooks, and containerized Docker services.",
    impact:
      "Cut database query latency by 40%, streamlined checkout workflows, and delivered seamless multi-store client onboarding.",
    metric: "40%",
    metricLabel: "Latency Reduction",
    techStack: ["Next.js", "Node.js", "PostgreSQL", "Stripe", "Docker", "Firebase"],
    links: {
      demo: "https://baked-bliss-frontend.vercel.app/",
      code: "https://github.com/AkashZaheer045/BakedBliss-backend",
    },
  },
  {
    number: "02",
    title: "Study Buddy Platform",
    category: "Real-Time Collaboration",
    badge: "EdTech Platform",
    image: studyBlissImg,
    alt: "Study Buddy real-time educational collaboration platform",
    description:
      "EdTech collaboration platform connecting students and educators with real-time interactive workspaces, live bidding, and secure media exchange.",
    challenge:
      "Scaling real-time WebSockets to manage hundreds of simultaneous live sessions and chunked file uploads without latency spikes.",
    solution:
      "Engineered an event-driven Socket.IO architecture with resilient cloud file streaming via Multer and optimized teacher-student bidding pipelines.",
    impact:
      "Successfully scaled to 1,000+ concurrent active connections with sub-50ms message latency and zero dropped packet events.",
    metric: "1,000+",
    metricLabel: "Concurrent Users",
    techStack: ["React", "Node.js", "Socket.IO", "Firebase", "MongoDB", "Multer"],
    links: {
      demo: null,
      code: "https://github.com/AkashZaheer045/study-buddy",
    },
  },
  {
    number: "03",
    title: "AI Book Recommendation Store",
    category: "AI Backend & Recommendation",
    badge: "AI System",
    image: booksImg,
    alt: "AI Book Recommendation Store mobile and web platform",
    description:
      "Hybrid mobile and web bookstore featuring an intelligent ML conversational agent that recommends literature based on user reading patterns and preferences.",
    challenge:
      "High drop-off rates from static search filters and slow background processing for high-volume inventory updates across web and mobile apps.",
    solution:
      "Built a FastAPI and Node.js microservice integrating ML vector embeddings for semantic search, paired with an event-driven sync engine.",
    impact:
      "Achieved 95% recommendation relevance score and cut catalog search turnaround time down to sub-100ms across all endpoints.",
    metric: "95%",
    metricLabel: "Recommendation Accuracy",
    techStack: ["FastAPI", "Python", "Node.js", "Machine Learning", "Flutter", "Firebase"],
    links: {
      demo: null,
      code: "https://github.com/AkashZaheer045/Book-Enhancement-Store-Backend",
    },
  },
  {
    number: "04",
    title: "Wise Way Car Rental Platform",
    category: "Microservices & DevOps",
    badge: "Cloud Infrastructure",
    image: wiseWayImg,
    alt: "Wise Way Car Rental microservices platform",
    description:
      "High-throughput microservices vehicle reservation platform featuring live geospatial fleet tracking, instant reservation locks, and automated billing.",
    challenge:
      "Monolithic booking bottlenecks during peak reservation hours and inaccurate vehicle geolocation synchronization.",
    solution:
      "Decoupled into containerized Node.js microservices with PostgreSQL geospatial indexing and automated Stripe payment gateways.",
    impact:
      "Handles 500+ requests/second under heavy concurrency while maintaining 99.9% booking uptime and instant geospatial lookups.",
    metric: "500+",
    metricLabel: "Req/sec Handled",
    techStack: ["Node.js", "Microservices", "PostgreSQL", "Stripe", "Docker", "AWS"],
    links: {
      demo: null,
      code: "https://github.com/AkashZaheer045",
    },
  },
];

const ProjectCard = ({ project, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1, duration: 0.55 }}
    viewport={{ once: true }}
    className="group glass-card rounded-2xl sm:rounded-3xl overflow-hidden border border-border/50
               flex flex-col h-full min-w-0 hover:-translate-y-1.5 transition-all duration-500 shadow-soft hover:shadow-elevated"
  >
    {/* ── Image & Media Preview ── */}
    <div className="relative aspect-[16/9] w-full overflow-hidden bg-secondary/40 border-b border-border/50">
      <img
        src={project.image}
        alt={project.alt}
        loading="lazy"
        className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent pointer-events-none" />

      {/* Top Badges */}
      <div className="absolute top-3 sm:top-4 left-3 sm:left-4 right-3 sm:right-4 flex items-center justify-between gap-2 pointer-events-none">
        <span className="px-2.5 py-1 rounded-full bg-background/90 backdrop-blur-md border border-border/70 text-[10px] font-mono font-bold uppercase tracking-wider text-foreground shadow-sm">
          {project.category}
        </span>
        <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/90 backdrop-blur-md text-[10px] font-bold text-primary-foreground uppercase tracking-wider shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground animate-pulse" />
          {project.badge}
        </span>
      </div>

      {/* Metric Overlay Bottom Right */}
      <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 pointer-events-none">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-background/90 backdrop-blur-md border border-primary/30 shadow-lg">
          <div className="text-base sm:text-lg font-heading font-black text-primary leading-none tabular-nums">
            {project.metric}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground leading-none">
            {project.metricLabel}
          </div>
        </div>
      </div>
    </div>

    {/* ── Body Content ── */}
    <div className="p-5 sm:p-7 flex flex-col flex-grow justify-between gap-5 min-w-0">
      <div>
        {/* Title & Number */}
        <div className="flex items-start justify-between gap-3 mb-2.5">
          <h3 className="text-lg sm:text-xl font-bold text-foreground font-heading tracking-tight group-hover:text-primary transition-colors leading-snug">
            {project.title}
          </h3>
          <span className="text-2xl sm:text-3xl font-heading font-bold text-border/60 group-hover:text-primary/25 transition-colors leading-none select-none tabular-nums flex-shrink-0">
            {project.number}
          </span>
        </div>

        <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed mb-4">
          {project.description}
        </p>

        {/* Structured Challenge & Solution */}
        <div className="space-y-2.5 bg-secondary/30 border border-border/40 p-3.5 sm:p-4 rounded-xl text-xs">
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-md bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-[10px]" />
            </div>
            <div className="min-w-0">
              <span className="font-bold text-[10px] uppercase tracking-wider text-amber-500 block">Challenge</span>
              <p className="text-muted-foreground text-xs leading-relaxed">{project.challenge}</p>
            </div>
          </div>

          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
              <FontAwesomeIcon icon={faWrench} className="text-[10px]" />
            </div>
            <div className="min-w-0">
              <span className="font-bold text-[10px] uppercase tracking-wider text-primary block">Architecture</span>
              <p className="text-muted-foreground text-xs leading-relaxed">{project.solution}</p>
            </div>
          </div>

          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-md bg-emerald-500/10 text-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FontAwesomeIcon icon={faChartLine} className="text-[10px]" />
            </div>
            <div className="min-w-0">
              <span className="font-bold text-[10px] uppercase tracking-wider text-emerald-500 block">Result &amp; Impact</span>
              <p className="text-muted-foreground text-xs leading-relaxed">{project.impact}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer: Tech Stack + CTAs ── */}
      <div className="pt-2 flex flex-col gap-4">
        {/* Tech badges */}
        <div className="flex flex-wrap gap-1.5 min-w-0">
          {project.techStack.map((tech) => (
            <span
              key={tech}
              className="text-[10px] font-mono font-bold tracking-wider uppercase px-2.5 py-1 rounded-lg bg-secondary/80 text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 border border-border/60 group-hover:border-primary/20 transition-colors duration-300"
            >
              {tech}
            </span>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-3 border-t border-border/50">
          {project.links?.demo ? (
            <a
              href={project.links.demo}
              target="_blank"
              rel="noreferrer"
              id={`project-${project.number}-demo`}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-xl text-xs font-bold hover:shadow-glow hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary shadow-sm"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[11px]" />
              Live Demo
            </a>
          ) : (
            <div className="w-full py-2.5 px-4 bg-secondary/40 text-muted-foreground rounded-xl text-xs font-bold border border-border/30 text-center">
              Private Enterprise Build
            </div>
          )}
        </div>
      </div>
    </div>
  </motion.div>
);

const Projects = ({ id = "case-studies" }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ margin: "-50px", once: true }}
    className="py-16 md:py-24 lg:py-32 bg-background overflow-hidden"
    id={id}
  >
    <div className="w-full relative z-10">
      {/* Section Header */}
      <div className="text-center mb-10 sm:mb-14 px-4 sm:px-5 md:px-10 lg:px-12 max-w-5xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary border border-border text-[10px] font-semibold mb-4 tracking-widest uppercase text-muted-foreground shadow-sm">
          Featured Builds &amp; Case Studies
        </div>
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 font-heading tracking-tight">
          Production Systems &amp; Case Studies
        </h2>
        <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
          Real client challenges solved with structured technical delivery. Each engagement follows a{" "}
          <span className="text-foreground font-bold">Challenge → Architecture → Impact</span> framework.
        </p>
      </div>

      {/* 2x2 Responsive Project Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-7xl mx-auto px-4 sm:px-5 md:px-10 lg:px-12">
        {PROJECTS.map((project, index) => (
          <ProjectCard key={project.number} project={project} index={index} />
        ))}
      </div>
    </div>
  </motion.section>
);

export default Projects;
